/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { convertToString, IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { getProductListLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import {
    ArrayExtensions,
    IOrderTemplateLinesState,
    OrderTemplateLine,
    ProductDetailsCriteria,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { LocalCache } from '../shared-utils/local-cache';
import { observable } from 'mobx';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetPaginatedOrderTemplateLinesInput implements IActionInput {
    public orderTemplateId: string;
    public queryResultSettings: IQueryResultSettings;
    public searchText: string;
    public getPrice: boolean = true;
    public getRating: boolean = true;
    public localCache: LocalCache | undefined;

    constructor(
        orderTemplateId: string,
        queryResultSettings: IQueryResultSettings,
        searchText?: string,
        getPrice: boolean = true,
        getRating: boolean = true,
        localCache?: LocalCache
    ) {
        this.orderTemplateId = orderTemplateId;
        this.queryResultSettings = queryResultSettings;
        this.searchText = searchText || '';
        this.getPrice = getPrice;
        this.getRating = getRating;
        this.localCache = localCache;
    }

    public getCacheKey = () =>
        `GetPaginatedOrderTemplateLines|${this.orderTemplateId}|${convertToString(this.queryResultSettings)}|${
            this.searchText
        }|${convertToString(this.getPrice)}|${convertToString(this.getRating)}`;

    public getCacheObjectType = () => 'GetPaginatedOrderTemplateLines';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 * @param orderTemplateId
 * @param searchText
 */
export const createGetPaginatedOrderTemplateLinesInput = (
    inputData: ICreateActionContext,
    orderTemplateId: string,
    searchText?: string,
    localCache?: LocalCache
): GetPaginatedOrderTemplateLinesInput => {
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;
    return new GetPaginatedOrderTemplateLinesInput(orderTemplateId, queryResultSettings, searchText, true, true, localCache);
};

export const getProductDetailsCriteriaFromActionInput = (inputData: GetPaginatedOrderTemplateLinesInput): ProductDetailsCriteria => {
    return {
        getPrice: inputData.getPrice ?? true,
        getRating: inputData.getRating ?? true
    };
};

/**
 * The action method for the GetPaginatedOrderTemplateLines data action.
 * @param input The action input.
 * @param context The action context.
 */
export async function getPaginatedOrderTemplateLinesAction(
    input: GetPaginatedOrderTemplateLinesInput,
    context: IActionContext
): Promise<IOrderTemplateLinesState> {
    if (!context.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }

    const orderTemplateLinesState: IOrderTemplateLinesState = {
        lines: [],
        totalCount: 0
    };
    const promise = getProductListLinesAsync(
        {
            callerContext: context,
            queryResultSettings: input.queryResultSettings,
            bypassCache: 'get'
        },
        input.orderTemplateId,
        input.searchText
    );
    const productListLines = await promise;

    if (ArrayExtensions.hasElements(productListLines)) {
        orderTemplateLinesState.totalCount = promise.metadata.count || productListLines.length;

        let ids = productListLines.map(x => x.ProductId).filter(Boolean) as number[];
        let simpleProducts = await input.localCache?.getProducts(ids);

        if (!simpleProducts || simpleProducts.length !== ids.length) {
            // Filter out cached prices
            ids = ids.filter(x => !simpleProducts?.find(y => y.RecordId === x));
            const moreSimpleProducts = await getByIdsAsync(
                { callerContext: context },
                context.requestContext.apiSettings.channelId,
                ids,
                null,
                context.requestContext.apiSettings.catalogId);

            if (!simpleProducts) {
                simpleProducts = observable.array();
            }
            simpleProducts.push(...moreSimpleProducts);

            if (input.localCache) {
                await input.localCache.putProducts(simpleProducts);
            }
        }

        productListLines.forEach(listLine => {
            if (listLine.ProductId) {
                orderTemplateLinesState.lines.push(new OrderTemplateLine(listLine));
            }
        });

        if (ArrayExtensions.hasElements(simpleProducts)) {
            orderTemplateLinesState.lines.forEach(line => {
                const simpleProduct = simpleProducts!.find(
                    product => product.RecordId === line.productListLine.ProductId
                );
                if (!simpleProduct) {
                    context.telemetry.error(
                        '[getPaginatedOrderTemplateLinesAction] Unable to find product info for an order template line.'
                    );
                    return;
                }
                line.fullProduct = { ProductDetails: simpleProduct };
            });

            return orderTemplateLinesState;
        }
        context.trace(orderTemplateLinesState.lines.length.toString());
        return orderTemplateLinesState;
    }

    return orderTemplateLinesState;
}

/**
 * The GetPaginatedOrderTemplateLines Data Action
 * Returns order templates.
 */
export const getPaginatedOrderTemplateLinesActionDataAction = createObservableDataAction<IOrderTemplateLinesState>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-paginated-order-template-lines',
    action: getPaginatedOrderTemplateLinesAction as IAction<IOrderTemplateLinesState>,
    input: createGetPaginatedOrderTemplateLinesInput
});

export default getPaginatedOrderTemplateLinesActionDataAction;
